/**
 * Centralized feature flags for VYV.
 * Read from Vite env vars (VITE_*) resolved at web/native build time; changing them requires a new build.
 * Defaults are SAFE (false) — enable explicitly via env when ready.
 *
 * Example usage (will be wired in future tasks):
 *
 *   import { useFeatureFlag } from '@/hooks/useFeatureFlag';
 *
 *   function CreateAvatarButton() {
 *     const metapersonOn = useFeatureFlag('METAPERSON_ENABLED');
 *     if (!metapersonOn) {
 *       return null; // Premium users still see default avatar
 *     }
 *     return <button onClick={openMetaPersonCreator}>...</button>;
 *   }
 */

import { isConnectionComingSoon } from "./connectionAvailability";

const readBoolFlag = (envValue: string | undefined, defaultValue: boolean): boolean => {
  if (envValue === undefined) return defaultValue;
  return envValue === 'true' || envValue === '1';
};

export const FEATURE_FLAGS = {
  EXPANSION_CATALOG_ENABLED: readBoolFlag(import.meta.env.VITE_EXPANSION_CATALOG_ENABLED, false),
  DROPBOX_ENABLED: readBoolFlag(import.meta.env.VITE_DROPBOX_ENABLED, false),
  TODOIST_ENABLED: readBoolFlag(import.meta.env.VITE_TODOIST_ENABLED, false),
  TRELLO_ENABLED: readBoolFlag(import.meta.env.VITE_TRELLO_ENABLED, false),
  SLACK_ENABLED: readBoolFlag(import.meta.env.VITE_SLACK_ENABLED, false),
  MICROSOFT_TEAMS_ENABLED: readBoolFlag(import.meta.env.VITE_MICROSOFT_TEAMS_ENABLED, false),
  PROACTIVE_ASSISTANT_ENABLED: readBoolFlag(import.meta.env.VITE_PROACTIVE_ASSISTANT_ENABLED, false),
  DOCUMENT_CAREER_ENABLED: readBoolFlag(import.meta.env.VITE_DOCUMENT_CAREER_ENABLED, false),
  PERSONAL_ACTION_ENGINE_ENABLED: readBoolFlag(import.meta.env.VITE_PERSONAL_ACTION_ENGINE_ENABLED, false),
  /**
   * ⚠️ FLAG DE RENDER, NÃO DE CRIAÇÃO (separado em 24/08, finding 831ad829).
   * getAvatarProvider() consulta este flag para decidir se renderiza o GLB
   * salvo do usuário. TODAS as 1.683 linhas de user_avatars têm
   * provider='metaperson' — inclusive as 1.556 ativas do catálogo prerendered
   * — e o fallback default aponta para /avatars/*.glb que NÃO existe no repo
   * (404). Desligar isto derruba o avatar de 1.556 pessoas.
   * ⛔ NÃO desligar para esconder o botão de criação — para isso existe
   * METAPERSON_CREATE_ENABLED abaixo. Em produção este flag está true desde
   * 25/05 e deve continuar true.
   */
  METAPERSON_ENABLED: readBoolFlag(
    import.meta.env.VITE_METAPERSON_ENABLED,
    false
  ),
  /**
   * CRIAÇÃO/edição de avatar MetaPerson via Creator embutido (metaperson-token).
   * Nasceu da separação do flag acima (831ad829): o gate do BOTÃO agora é este,
   * e só este — canCreateMetaPersonAvatar(), o card "criar do zero" no
   * OnboardingAvatar e o "Editar rosto" no SettingsAvatars leem daqui.
   * Default false: a credencial Enterprise (AVATARSDK_*) morre com a fatura de
   * 25/08 e o Creator embutido passaria a dar 502 — botão visível levando a
   * erro é pior que botão ausente. Religar = VITE_METAPERSON_CREATE_ENABLED=true
   * (Vercel para web; .env local para builds nativos — flag no Vercel NÃO
   * alcança iOS/Android).
   */
  METAPERSON_CREATE_ENABLED: readBoolFlag(
    import.meta.env.VITE_METAPERSON_CREATE_ENABLED,
    false
  ),
  /**
   * PIX checkout flow for Free → Premium upgrade.
   * When false, Pricing CTA shows "Em breve" toast (launch dark default).
   * When true, CTA navigates to /checkout/premium → asaas-create-subscription.
   */
  PIX_PAYMENT_ENABLED: readBoolFlag(
    import.meta.env.VITE_PIX_PAYMENT_ENABLED,
    false
  ),
  /**
   * Native In-App Purchase (RevenueCat / StoreKit) on iOS.
   * When true (default), iOS native gets the IAP purchase path via RevenueCat.
   * Distinct from PIX_PAYMENT_ENABLED (web/Asaas) and from isPaymentUiBlocked()
   * (which keeps all EXTERNAL payment surfaces hidden on iOS per Apple 3.1.1).
   * Kill-switch via VITE_NATIVE_IAP_ENABLED=false without a native rebuild.
   */
  NATIVE_IAP_ENABLED: readBoolFlag(
    import.meta.env.VITE_NATIVE_IAP_ENABLED,
    true
  ),
  /**
   * Card do Spotify em /settings/connections.
   *
   * DESLIGADO por política de plataforma, não por bug (decisão Rafa 30/07):
   * todo app novo do Spotify nasce em Development Mode — 5 usuários numa
   * allowlist manual; quem está fora recebe 403 em toda chamada. O Extended
   * Quota Mode exige organização E 250.000 MAU; o VYV tem 1.301 usuários.
   * A integração não pode servir a base hoje.
   *
   * O código continua inteiro e correto: spotify-oauth-init/callback/pull
   * seguem deployadas, o hook e o card existem. Reativar quando houver
   * escala = VITE_SPOTIFY_ENABLED=true (ou trocar o default abaixo).
   * Os secrets SPOTIFY_OAUTH_CLIENT_ID/SECRET não existem de propósito.
   */
  SPOTIFY_ENABLED: readBoolFlag(
    import.meta.env.VITE_SPOTIFY_ENABLED,
    false
  ),
  /** Gmail Email Executor. Connection stays disabled until Google OAuth consent/redirect config is approved. */
  GMAIL_ENABLED: readBoolFlag(
    import.meta.env.VITE_GMAIL_ENABLED,
    false
  ),
  /** Outlook Mail Email Executor. Connection stays disabled until Microsoft Entra redirect/scopes are approved. */
  OUTLOOK_MAIL_ENABLED: readBoolFlag(
    import.meta.env.VITE_OUTLOOK_MAIL_ENABLED,
    false
  ),
  /** Google Drive Files Executor. Connection stays disabled until Google console scopes/redirect are approved. */
  GOOGLE_DRIVE_ENABLED: readBoolFlag(
    import.meta.env.VITE_GOOGLE_DRIVE_ENABLED,
    false
  ),
  /** OneDrive Files Executor. Connection stays disabled until Microsoft Entra redirect/scopes are approved. */
  ONEDRIVE_ENABLED: !isConnectionComingSoon("onedrive") && readBoolFlag(
    import.meta.env.VITE_ONEDRIVE_ENABLED,
    false
  ),
  /** Files/Knowledge tool routing in Mirror. Provider cards have their own flags. */
  FILES_EXECUTOR_ENABLED: readBoolFlag(
    import.meta.env.VITE_FILES_EXECUTOR_ENABLED,
    false
  ),
  // V3 connection/actions stay disabled until OAuth/device activation is verified.
  GOOGLE_CONTACTS_ENABLED: readBoolFlag(import.meta.env.VITE_GOOGLE_CONTACTS_ENABLED, false),
  MICROSOFT_CONTACTS_ENABLED: readBoolFlag(import.meta.env.VITE_MICROSOFT_CONTACTS_ENABLED, false),
  MICROSOFT_TODO_ENABLED: readBoolFlag(import.meta.env.VITE_MICROSOFT_TODO_ENABLED, false),
  APPLE_CONTACTS_ENABLED: readBoolFlag(import.meta.env.VITE_APPLE_CONTACTS_ENABLED, false),
  APPLE_REMINDERS_ENABLED: readBoolFlag(import.meta.env.VITE_APPLE_REMINDERS_ENABLED, false),
  /** EventKit writes remain OFF until reviewed native acceptance. */
  APPLE_CALENDAR_WRITES_ENABLED: readBoolFlag(import.meta.env.VITE_APPLE_CALENDAR_WRITES_ENABLED, false),
  PEOPLE_TASKS_EXECUTOR_ENABLED: readBoolFlag(import.meta.env.VITE_PEOPLE_TASKS_EXECUTOR_ENABLED, false),
  CREATOR_CONNECTIONS_ENABLED: readBoolFlag(import.meta.env.VITE_CREATOR_CONNECTIONS_ENABLED, false),
} as const;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;
